import { mapActions } from "pinia";
import { useAppStore } from "@/stores/app";
import { useAuthStore } from "@/stores/auth";
import { useUIStore } from "@/stores/ui";

import { getHeaders } from "../plugins/rest/common";
import { processErrors } from "../plugins/rest/errors";

const BASE_URL = import.meta.env.VITE_APP_BACKEND;

export default {
    methods: {
        ...mapActions(useUIStore, ["startWorking", "finishWorking"]),
        ...mapActions(useAuthStore, ["clearAuth"]),

        async $request(
            endpoint,
            data = undefined,
            filename = undefined,
            hideOverlay = false,
            overlayMessage = undefined,
        ) {
            const appStore = useAppStore();

            const headers = getHeaders();
            if (filename) {
                headers["Content-Type"] = undefined;
            }

            if (!hideOverlay) this.startWorking(overlayMessage);
            try {
                let response;
                try {
                    let body = data;
                    if (body) {
                        body = JSON.stringify(body);
                    }
                    response = await fetch(`${BASE_URL}${endpoint}`, {
                        method: "POST",
                        headers,
                        body,
                    });
                    appStore.connected = true;
                } catch (_) {
                    appStore.connected = false;
                    return;
                }

                const status = response.status;
                let json;

                if (status === 200) {
                    if (filename) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", filename);
                        document.body.appendChild(link);
                        link.click();

                        return;
                    }
                    json = await response.json();
                    return json.body;
                } else {
                    json = await response.json();
                    processErrors(status, json);
                }
            } finally {
                if (!hideOverlay) this.finishWorking();
            }
        },

        async $upload(file, overlay = true) {
            if (overlay) this.startWorking();

            const headers = getHeaders();
            headers["Content-Type"] = undefined;

            const form = new FormData();
            form.append("file", file);
            try {
                const response = await fetch(`${BASE_URL}/t3/documents/upload/document`, {
                    method: "POST",
                    headers,
                    body: form,
                });

                const status = response.status;
                const json = await response.json();

                if (status === 200) {
                    return json;
                }

                processErrors(status, json);
            } finally {
                if (overlay) this.finishWorking();
            }
        },

        async $uploadFile(file, overlay = true) {
            if (overlay) this.startWorking();

            const headers = getHeaders();
            delete headers["Content-Type"];

            const form = new FormData();
            form.append("file", file);
            try {
                const response = await fetch(`${BASE_URL}/internal/upload`, {
                    method: "POST",
                    headers,
                    body: form,
                });

                const status = response.status;
                const json = await response.json();

                if (status === 200) {
                    return json;
                }

                processErrors(status, json);
            } finally {
                if (overlay) this.finishWorking();
            }
        },

        async $download(bucket, uid, filename = undefined) {
            const endpoint = `${BASE_URL}/t3/storages/download/${bucket}/${uid}`;
            await this.$downloadBucketFile(endpoint, filename);
        },

        async $downloadBucketFile(endpoint, filename = undefined, hideOverlay = false, overlayMessage = undefined) {
            if (!hideOverlay) this.startWorking(overlayMessage);
            try {
                const headers = getHeaders();
                headers["Content-Type"] = undefined;

                const response = await fetch(endpoint, {
                    headers,
                });
                const status = response.status;

                if (status === 200) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", filename || "unknown");
                    document.body.appendChild(link);
                    link.click();
                }
            } finally {
                if (!hideOverlay) this.finishWorking();
            }
        },
    },
};
